import { useInCreateDialog } from 'components/create-dialog/CreateDialogContext';
import CheckboxInput from 'components/forms/inputs/rhf-inputs/CheckboxInput';
import DateInput from 'components/forms/inputs/rhf-inputs/DateInput';
import DecimalInput from 'components/forms/inputs/rhf-inputs/DecimalInput';
import DynamicSelectInput from 'components/forms/inputs/rhf-inputs/DynamicSelectInput';
import SelectInput from 'components/forms/inputs/rhf-inputs/SelectInput';
import TextAreaInput from 'components/forms/inputs/rhf-inputs/TextAreaInput';
import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { openCasesFilter } from 'components/tables/list-table/filter-pieces/filterConstants';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { cn } from 'utils/cn';
import { getLabelWithDescription } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators, renderMatterOptionWithRestrictedSelection } from 'utils/matterOptionRenderer';
import { partyNumberString } from 'utils/partyNameUtil';
import { ExpenseFormState } from './useExpenseForm';

type FieldsProps = { form: ExpenseFormState };

// The form lists its fields in an order that mixes the two sections, so each run of fields is its own piece.
// On the record page each field is a cell of its section's grid, where the read view has its row: a wrapper that
// lays several fields out steps aside there, and the case with its client line is one cell.

/**
 * The case the expense is booked on, with its client under it. In the create dialog the field is drawn in the
 * Required section, away from the wrapper here, so the client's line is handed to the field and stays in its
 * cell, under its control. `disabled` where the case is given, as on an invoice.
 */
export const MatterField = ({ form, disabled }: FieldsProps & { disabled?: boolean }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { isPartyNumberUsageEnabled, selectedClientName } = form;
    const inCreateDialog = useInCreateDialog();
    const client = selectedClientName ? (
        <>
            {t('expenses.form.client')}: {selectedClientName}
        </>
    ) : null;
    const inSection = useInRecordSection();
    return (
        <div className={inSection ? 'form-field' : undefined}>
            <DynamicSelectInput
                setValue={setValue}
                control={control}
                label="expenses.form.case-client"
                required
                disabled={disabled}
                // A case given by the page is fixed: no clear to suggest it can change.
                clearable={!disabled}
                name="matter"
                paginated={{
                    fetch: useLazyGetMattersListQuery,
                    filters: openCasesFilter,
                    searchField: ['fullName', 'clientParty.name'],
                    sort: ['clientParty.name,asc', 'name,asc'],
                }}
                groupByField="clientParty.name"
                getOptionLabel="fullName"
                getOptionIdentifier="id"
                placeholder="expenses.form.case-client"
                getSelectedValueStartAdornment={renderMatterIndicators}
                customOptionRenderer={(option, props, getValue) =>
                    renderMatterOptionWithRestrictedSelection(
                        option,
                        props,
                        getValue,
                        true,
                        'dashboard.tooltips.expense-blocked'
                    )
                }
                getGroupStartAdornment={
                    isPartyNumberUsageEnabled
                        ? (_group, options) => {
                              const groupClient = options?.[0]?.clientParty;
                              return groupClient ? partyNumberString(groupClient, isPartyNumberUsageEnabled) : null;
                          }
                        : undefined
                }
                bottomRightLabelComponent={
                    inCreateDialog && client ? (
                        <div className="text-muted-foreground w-full text-xs">{client}</div>
                    ) : undefined
                }
            />
            {!inCreateDialog && client ? (
                // One line in a section, as the read view has the client on one: the cell is as tall in both.
                <div
                    className={cn('text-fg pt-1 text-sm', inSection && 'truncate')}
                    title={(inSection && selectedClientName) || undefined}
                >
                    {client}
                </div>
            ) : null}
        </div>
    );
};

/** The expense type, and the amount in the currency it was paid in, under `amountLabel` where the form names it. */
export const TypeAndAmountFields = ({
    form,
    amountLabel = 'expenses.form.amount',
}: FieldsProps & { amountLabel?: string }): JSX.Element => {
    const { control, setValue } = form.methods;
    const { classifiers, currencyOptions, expenseTypeOptions, isNegativeAmountAllowed } = form;
    const inSection = useInRecordSection();
    return (
        <>
            <SelectInput
                setValue={setValue}
                control={control}
                label="expenses.form.expense-type"
                required
                name="expenseType"
                options={expenseTypeOptions}
                getOptionLabel={getLabelWithDescription(classifiers['EXPENSE_TYPE'])}
                getOptionIdentifier={(option) => String(option.value)}
                placeholder="expenses.form.expense-type"
            />
            <div className={inSection ? 'contents' : 'flex flex-wrap gap-4'}>
                <div className={inSection ? 'contents' : 'flex-1'}>
                    <DecimalInput
                        name="expenseAmount"
                        control={control}
                        label={amountLabel}
                        placeholder={amountLabel}
                        required
                        allowNegative={isNegativeAmountAllowed}
                    />
                </div>
                <div className={inSection ? 'contents' : 'flex-1'}>
                    <SelectInput
                        setValue={setValue}
                        control={control}
                        label="expenses.form.currency"
                        required
                        name="currencyType"
                        options={currencyOptions}
                        getOptionLabel="label"
                        getOptionIdentifier="value"
                        placeholder="expenses.form.currency"
                    />
                </div>
            </div>
        </>
    );
};

/** Who the expense is booked for. */
export const EmployeeField = ({ form }: FieldsProps): JSX.Element => {
    const { control, setValue } = form.methods;
    return (
        <DynamicSelectInput
            setValue={setValue}
            control={control}
            label="expenses.form.added-by"
            required
            name="employee"
            paginated={{
                fetch: useLazyGetEmployeesListWithFiltersQuery,
                searchField: 'fullName',
            }}
            getOptionLabel="name"
            getOptionIdentifier="id"
            placeholder="expenses.form.added-by"
        />
    );
};

/** The day of the expense and its details. */
export const DateAndDescriptionFields = ({ form }: FieldsProps): JSX.Element => {
    const { control } = form.methods;
    const inSection = useInRecordSection();
    return (
        <>
            <div className={inSection ? 'contents' : 'flex'}>
                <div className={inSection ? 'contents' : 'flex-1'}>
                    <DateInput control={control} name="expenseDate" label="date" required maxDate={new Date()} />
                </div>
            </div>
            <TextAreaInput
                name="description"
                control={control}
                label="expenses.form.details-of-expense"
                placeholder="expenses.form.details-of-expense"
            />
        </>
    );
};

/** Non-billable, which a non-billable case holds on. */
export const NonBillableField = ({ form }: FieldsProps): JSX.Element => (
    <CheckboxInput
        control={form.methods.control}
        name="isNonBillable"
        label="expenses.form.is-non-billable"
        disabled={form.isMatterNonBillable}
    />
);

/** The Expense section: what was spent, how much, when. */
export const ExpenseFields = ({ form }: FieldsProps): JSX.Element => (
    <>
        <TypeAndAmountFields form={form} />
        <DateAndDescriptionFields form={form} />
    </>
);

/** The Booking section: the case, the employee and billability. */
export const BookingFields = ({ form }: FieldsProps): JSX.Element => (
    <>
        <MatterField form={form} />
        <EmployeeField form={form} />
        <NonBillableField form={form} />
    </>
);
